import React from 'react';
import Localize from 'react-intl-universal';
import { useSearchParams } from 'react-router-dom';

import { get } from 'lodash-es';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {
  formatLongDateTimeStringToDateTimeObject,
  getPricingDetails,
  seatsInfo
} from '@common/Util.jsx';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import { canShowWebinarUrl, getVenues } from '@pages/EventDetails/Util.jsx';

import EventTopics from './EventTopics.jsx';
import ShowDirectionDialog from './ShowDirectionDialog.jsx';

const StyledIcon = styled(Box)(({ theme }) => ({
  width: '2.5rem',
  height: '2rem',
  backgroundColor: theme.palette.grey[300],
  display: ' flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '40%'
}));

const StyledGrid = styled(Grid)(() => ({ '&.MuiGrid-item': { paddingLeft: '2rem' } }));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '0.5rem',
  minHeight: '3rem',
  gap: theme.spacing(2)
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  wordWrap: 'break-word',
  textOverflow: 'ellipsis',
  fontWeight: 'bold'
}));

const StyledSubTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[200],
  fontSize: '11px'
}));

const StyledSubTitleTooltip = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
  marginLeft: '1.5rem'
}));

const StyledLabel = styled(Box)(({ theme }) => ({
  cursor: 'default',
  color: theme.palette.primary.dark,
  borderColor: theme.palette.primary.dark,
  border: '1px solid',
  padding: '0.5833em 0.833em',
  borderRadius: '0.28571429rem',
  width: 'fit-content'
}));

const StyledPopupList = styled(List)(({ theme }) => ({
  color: theme.palette.primary.background,
  marginTop: '10px',
  marginBottom: '10px',
  minWidth: '200px',
  paddingTop: '2px',
  paddingBottom: '2px',
  fontSize: '12px',
  listStylePosition: 'inside',
  display: 'block'
}));

const StyledData = styled(Box)(({ theme }) => ({
  flex: 2,
  minWidth: 0,
  wordWrap: 'break-word',
  color: theme.palette.primary.contrastText
}));

const ColumnWithTooltip = ({ contentArray = [''], location = false }) => {
  return (
    <Box sx={{ display: 'flex', width: '100%', minWidth: 0, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
        {location ? (
          <Typography sx={{ marginBottom: 0 }}>{contentArray[0]}</Typography>
        ) : (
          <>
            <Tooltip title={get(contentArray[0], 'name', '')} arrow>
              <Typography
                sx={{
                  mb: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {get(contentArray[0], 'name', '')}
              </Typography>
            </Tooltip>
            {get(contentArray[0], 'street', '') && (
              <StyledSubTitle sx={{ marginBottom: 0 }}>
                {`${get(contentArray[0], 'street', '')}${' '}
              ${get(contentArray[0], 'streetNumber', '')}`}
              </StyledSubTitle>
            )}
            {get(contentArray[0], 'city', '') && (
              <StyledSubTitle>{get(contentArray[0], 'city', '')}</StyledSubTitle>
            )}
            {get(contentArray[0], 'postCode', '') && (
              <StyledSubTitle>{get(contentArray[0], 'postCode', '')}</StyledSubTitle>
            )}
          </>
        )}
      </Box>
      <Box>
        {contentArray.length > 1 && (
          <Tooltip
            enterTouchDelay={0}
            title={
              <StyledPopupList>
                {contentArray.slice(1).map((item, index) => (
                  <ListItem
                    sx={{ listStyleType: 'disc', display: 'list-item', pl: 2 }}
                    key={`${index}-${item}`}
                  >
                    {location ? (
                      item
                    ) : (
                      <>
                        <Typography sx={{ marginBottom: 0, display: 'inline' }}>
                          {item?.name}
                        </Typography>
                        {item?.street && (
                          <StyledSubTitleTooltip sx={{ marginBottom: 0 }}>
                            {`${item?.street}${' '}${item?.streetNumber}`}
                          </StyledSubTitleTooltip>
                        )}
                        <StyledSubTitleTooltip>{item?.city}</StyledSubTitleTooltip>
                        {item?.postCode && (
                          <StyledSubTitleTooltip>{item?.postCode}</StyledSubTitleTooltip>
                        )}
                      </>
                    )}
                  </ListItem>
                ))}
              </StyledPopupList>
            }
            placement='bottom'
          >
            <StyledIcon>
              <Icon sx={{ fontSize: '0.8rem' }} fontSize='small'>
                add
              </Icon>
              <span style={{ marginLeft: '5px', fontSize: '0.8rem' }}>
                {contentArray.length - 1}
              </span>
            </StyledIcon>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const Information = ({ event = {}, configuration, isBooking = false, minHeight = '' }) => {
  const locationNames = !event?.venues?.length
    ? []
    : event.venues.map((el) => `${el?.city ? el.city + ', ' : ''}${el?.country}`);

  const { eventPrice, eventCurrency, isEventPrice0 } = getPricingDetails(event);
  const [contextData] = useGlobalContext();
  const [searchParams] = useSearchParams();

  const venues = getVenues(event);
  const showWebinarUrl = canShowWebinarUrl(event, searchParams.get('participant'));

  return (
    <Grid container spacing={2} sx={{ minHeight: minHeight }}>
      <StyledGrid item xs={12} md={6} sx={{ color: 'black', paddingLeft: '2rem' }}>
        {configuration?.EventDetailsInfoTab.items
          .sort((a, b) => a.order - b.order)
          .map((item, index) => {
            switch (item.name) {
              case 'startDate':
                return (
                  <StyledBox key={index} data-testid={'infoDateContainer'}>
                    <StyledTypography>{Localize.get('EventDetails.startDate')}</StyledTypography>
                    <Divider orientation='vertical' flexItem />
                    <StyledData>
                      {formatLongDateTimeStringToDateTimeObject(event.startDateTime)}
                    </StyledData>
                  </StyledBox>
                );
              case 'endDate':
                return (
                  <StyledBox key={index} data-testid={'infoDateContainer'}>
                    <StyledTypography>{Localize.get('EventDetails.endDate')}</StyledTypography>
                    <Divider orientation='vertical' flexItem />
                    <StyledData>
                      {formatLongDateTimeStringToDateTimeObject(event.endDateTime)}
                    </StyledData>
                  </StyledBox>
                );
              case 'location':
                return (
                  !!locationNames.length && (
                    <StyledBox key={index}>
                      <StyledTypography> {Localize.get('App.location')}</StyledTypography>
                      <Divider orientation='vertical' flexItem />
                      <StyledData>
                        {locationNames.length > 1 ? (
                          <ColumnWithTooltip
                            contentArray={locationNames}
                            colorConfig={configuration?.EventsDetailsContent?.popupColors}
                            location={true}
                          />
                        ) : (
                          locationNames[0]
                        )}
                      </StyledData>
                    </StyledBox>
                  )
                );
              case 'venue':
                return (
                  venues?.length > 0 && (
                    <StyledBox key={index}>
                      <StyledTypography> {Localize.get('EventDetails.venue')}</StyledTypography>
                      <Divider orientation='vertical' flexItem />
                      <StyledData>
                        {venues.length > 1 ? (
                          <ColumnWithTooltip
                            contentArray={venues}
                            colorConfig={configuration?.EventsDetailsContent?.popupColors}
                          />
                        ) : (
                          <>
                            <Typography>{venues[0]?.name ?? ''}</Typography>
                            <StyledSubTitle>
                              {`${venues[0]?.street ?? ''} ${venues[0]?.streetNumber ?? ''}`}
                            </StyledSubTitle>
                            <StyledSubTitle>{venues[0]?.city ?? ''}</StyledSubTitle>
                            <StyledSubTitle>{venues[0]?.postCode ?? ''}</StyledSubTitle>
                          </>
                        )}
                        <ShowDirectionDialog venues={event?.venues} />
                      </StyledData>
                    </StyledBox>
                  )
                );
              case 'webinar':
                return (
                  showWebinarUrl && (
                    <StyledBox key={index}>
                      <StyledTypography>
                        {Localize.get('CustomerLoyaltyEvents.webinar')}
                      </StyledTypography>
                      <Divider orientation='vertical' flexItem />
                      <StyledData>
                        <a
                          href={event?.webinarUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{ textDecoration: 'underline' }}
                        >
                          {event.webinarUrl}
                        </a>
                      </StyledData>
                    </StyledBox>
                  )
                );
              case 'address':
                return (
                  event?.address && (
                    <StyledBox key={index} data-testid={'infoDateContainer'}>
                      <StyledTypography>{Localize.get('EventDetails.address')}</StyledTypography>
                      <Divider orientation='vertical' flexItem />
                      <StyledData>{event.address}</StyledData>
                    </StyledBox>
                  )
                );
              case 'lang':
                return (
                  get(event, 'nativeLanguages') && (
                    <StyledBox key={index} data-testid={'infoDateContainer'}>
                      <StyledTypography> {Localize.get('App.lang')}</StyledTypography>
                      <Divider orientation='vertical' flexItem />
                      <StyledData>
                        {event.nativeLanguages?.length > 1 ? (
                          <ColumnWithTooltip
                            contentArray={event.nativeLanguages?.map((item) => item?.value)}
                            colorConfig={configuration?.EventsDetailsContent?.popupColors}
                            location={true}
                          />
                        ) : (
                          get(event, 'nativeLanguages[0].value', '')
                        )}
                      </StyledData>
                    </StyledBox>
                  )
                );
              case 'prices':
                return (
                  <StyledBox key={index} data-testid={'infoDateContainer'}>
                    <StyledTypography>{Localize.get('EventDetails.price')}</StyledTypography>
                    <Divider orientation='vertical' flexItem />
                    <StyledData>
                      <StyledLabel>
                        {isEventPrice0
                          ? Localize.get('CustomerLoyaltyEvents.free')
                          : `${eventPrice} ${eventCurrency}`}
                      </StyledLabel>
                    </StyledData>
                  </StyledBox>
                );
              case 'seats':
                return (
                  !isBooking && (
                    <StyledBox key={index} data-testid={'infoDateContainer'}>
                      <StyledTypography>
                        {Localize.get('CourseDetails.Seats').replace(':', '')}
                      </StyledTypography>
                      <Divider orientation='vertical' flexItem />
                      <StyledData>
                        {seatsInfo(event?.availableSeats, contextData?.isSeatsNumber)}
                      </StyledData>
                    </StyledBox>
                  )
                );
              case 'waitingList':
                return (
                  event?.hasWaitingList && (
                    <StyledBox key={index} data-testid={'infoDateContainer'}>
                      <StyledTypography>
                        {Localize.get('LearningPathDetails.waitingList')}
                      </StyledTypography>
                      <Divider orientation='vertical' flexItem />
                      <StyledData>{Localize.get('MyAreaIltSessionsTab.available')}</StyledData>
                    </StyledBox>
                  )
                );
              case 'registrationUntil':
                return (
                  event?.registrationDeadline && (
                    <StyledBox key={index} data-testid={'infoDateContainer'}>
                      <Tooltip title={Localize.get('CustomerLoyaltyEvents.registrationUntil')}>
                        <StyledTypography>
                          {Localize.get('CustomerLoyaltyEvents.registrationUntil')}
                        </StyledTypography>
                      </Tooltip>
                      <Divider orientation='vertical' flexItem />
                      <StyledData>
                        {formatLongDateTimeStringToDateTimeObject(event.registrationDeadline)}
                      </StyledData>
                    </StyledBox>
                  )
                );
              case 'cancellationUntil':
                return (
                  event?.cancellationDeadline && (
                    <StyledBox key={index} data-testid={'infoDateContainer'}>
                      <StyledTypography>
                        {Localize.get('CustomerLoyaltyEvents.cancellationUntil')}
                      </StyledTypography>
                      <Divider orientation='vertical' flexItem />
                      <StyledData>
                        {formatLongDateTimeStringToDateTimeObject(event.cancellationDeadline)}
                      </StyledData>
                    </StyledBox>
                  )
                );
            }
          })}
      </StyledGrid>
      <Grid item xs={12} md={6} sx={{ mb: 1, textAlign: 'left' }}>
        {event?.agendaNote && <EventTopics agendaNote={event?.agendaNote} />}
      </Grid>
    </Grid>
  );
};

export default Information;
